import * as api from "../api";

import {
  createDebriefState,
  uploadDeBriefState,
  uploadModalAtom,
} from "../store";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { fetchAuthSession } from "aws-amplify/auth";
import { hotjar } from "react-hotjar";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useSetAtom } from "jotai";

export function userOptions() {
  return queryOptions({
    queryKey: ["user"],
    queryFn: async () => {
      const { tokens } = await fetchAuthSession();
      const { idToken } = tokens;
      const { payload } = idToken;

      return {
        email: payload.email,
        username: payload.username,
        user_agreement_signature_needed:
          payload?.user_agreement_signature_needed === "true",
        user_registration_needed: payload?.user_registration_needed === "true",
        intercom_hash: payload?.intercom_hash,
      };
    },
  });
}

export function doctrinaNamesOptions() {
  return queryOptions({
    queryKey: ["doctrina", "names"],
    queryFn: async () => await api.get(`/courts/names`),
  });
}

export function listSubjectsOptions(type, id) {
  return queryOptions({
    queryKey: ["subjects", type, id],
    queryFn: async () => await api.get(`/briefs/${type}-subjects/${id}`),
  });
}

export function listCategoriesOptions(type, id) {
  return queryOptions({
    queryKey: ["categories", type, id],
    queryFn: async () => await api.get(`/briefs/${type}-categories/${id}`),
  });
}

export function briefOptions(id) {
  return queryOptions({
    queryKey: ["brief", id],
    queryFn: async () => await api.get(`/briefs/${id}`),
  });
}

export function useCreateBrief({ shouldRedirect = false, brief_id }) {
  const [, setSearchParams] = useSearchParams();
  const setModalOpen = useSetAtom(uploadModalAtom(brief_id));
  const setState = useSetAtom(createDebriefState);
  const reset = useSetAtom(uploadDeBriefState);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: ["brief"],
    mutationFn: async ({ files, ...rest }) => {
      const file = files[0];
      const { name: filename } = file;

      const { url, fields, brief_id } = await api.post("/briefs", {
        body: JSON.stringify({
          ...rest,
          filename,
        }),
      });

      const formData = new FormData();

      Object.entries(fields).forEach((entry) => {
        const [key, value] = entry;
        formData.append(key, value);
      });

      formData.append("file", file);

      await api.file(url, { method: "POST", body: formData });

      return { brief_id };
    },
    onSuccess: async ({ brief_id }) => {
      await queryClient.invalidateQueries(["briefs"]);
      await queryClient.invalidateQueries(["brief", brief_id]);

      if (!shouldRedirect) {
        setSearchParams({});
        setModalOpen(false);
        hotjar.event("debrief-new-version-upload");
      }

      if (shouldRedirect) {
        setState(() => ({ step: 1, judge: null }));
        reset({ files: "", judge: "" });
        navigate(`/debrief/case/${brief_id}`);
      }
    },
  });
}
